/**
 * WR Beautiful — Cart Drawer Web Component
 *
 * Features:
 * - Slide-in from right with backdrop overlay
 * - iOS-safe body scroll lock (position: fixed + top offset)
 * - AJAX add/update/remove via Section Rendering API
 * - Free shipping progress bar with kuruş math
 * - Event delegation for close button (survives DOM re-render)
 * - Turkish error messages with English→Turkish mapping safety net
 * - Auto-open on add-to-cart
 * - Live header cart count badge update + bounce animation
 * - PubSub integration for cross-component events
 */

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.savedScrollY = 0;

    // Event delegation — works even after DOM re-render
    this.addEventListener('click', (evt) => {
      // Close button (delegated — survives section re-render)
      if (evt.target.closest('[data-cart-close]')) {
        evt.preventDefault();
        this.close();
        return;
      }

      // Overlay click to close
      if (evt.target.matches('#CartDrawer-Overlay')) {
        this.close();
        return;
      }

      // Quantity minus button
      const minusBtn = evt.target.closest('.cart-drawer__qty-btn[name="minus"]');
      if (minusBtn) {
        evt.preventDefault();
        this.handleQuantityButton(minusBtn, -1);
        return;
      }

      // Quantity plus button
      const plusBtn = evt.target.closest('.cart-drawer__qty-btn[name="plus"]');
      if (plusBtn) {
        evt.preventDefault();
        this.handleQuantityButton(plusBtn, 1);
        return;
      }

      // Sample select button (CART-06)
      const sampleBtn = evt.target.closest('[data-sample-add]');
      if (sampleBtn && !sampleBtn.disabled) {
        evt.preventDefault();
        this.handleSampleSelect(sampleBtn);
        return;
      }

      // Upsell quick-add button (CART-07)
      const upsellBtn = evt.target.closest('[data-upsell-add]');
      if (upsellBtn && !upsellBtn.disabled) {
        evt.preventDefault();
        this.handleUpsellAdd(upsellBtn);
        return;
      }
    });

    // Escape key to close
    this.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape') this.close();
    });

    this.setHeaderCartIconAccessibility();

    // Listen for quick-add cart:updated events (from product-card.liquid)
    // This auto-opens the drawer when items are added via quick-add buttons
    document.addEventListener('cart:updated', (evt) => {
      this.refreshAndOpen(evt.detail);
    });

    // Listen for PubSub cart updates (quantity changes, removals from CartItems)
    // to announce changes via aria-live region
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source === 'cart-items') {
          this.announceCartChange('updated');
        }
      });
    }
  }

  /**
   * Fetch fresh cart drawer + header badge HTML and auto-open.
   * Used when add-to-cart happens outside product-form.js flow (e.g., quick-add).
   * Fetches both sections in parallel so header cart count updates immediately.
   */
  refreshAndOpen(detail) {
    Promise.all([
      fetch(`${window.routes.cart_url}?section_id=cart-drawer`).then((r) => r.text()),
      fetch(`${window.routes.cart_url}?section_id=cart-icon-bubble`).then((r) => r.text()),
    ])
      .then(([drawerText, bubbleText]) => {
        // Update drawer inner content
        const drawerHtml = new DOMParser().parseFromString(drawerText, 'text/html');
        const sourceInner = drawerHtml.querySelector('.cart-drawer__inner');
        const targetInner = this.querySelector('.cart-drawer__inner');
        if (sourceInner && targetInner) {
          targetInner.innerHTML = sourceInner.innerHTML;
        }

        // Toggle empty state
        const cartDrawerEl = drawerHtml.querySelector('cart-drawer');
        const isEmpty = cartDrawerEl && cartDrawerEl.classList.contains('is-empty');
        this.classList.toggle('is-empty', !!isEmpty);

        // Update header cart count badge
        const bubbleEl = document.getElementById('cart-icon-bubble');
        if (bubbleEl && bubbleText) {
          const bubbleHtml = new DOMParser().parseFromString(bubbleText, 'text/html');
          const newContent = bubbleHtml.querySelector('.shopify-section');
          if (newContent) {
            bubbleEl.innerHTML = newContent.innerHTML;
          }
        }

        // Bounce the cart badge
        this.triggerBadgeBounce();

        // Announce cart change for screen readers
        this.announceCartChange('added');

        // Publish cart update event for other components
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-drawer',
            cartData: detail && detail.cart ? detail.cart : null,
          });
        }

        // Auto-open
        this.open();
      })
      .catch((e) => {
        console.error('Cart drawer refresh failed:', e);
      });
  }

  /**
   * Handle quantity +/- button clicks
   */
  handleQuantityButton(button, delta) {
    const index = button.dataset.index;
    const input = this.querySelector(`#Drawer-quantity-${index}`);
    if (!input) return;

    const currentVal = parseInt(input.value, 10) || 1;
    const newVal = currentVal + delta;

    if (newVal < 1) {
      // Remove item when going below 1
      const cartItems = this.querySelector('cart-drawer-items');
      if (cartItems) cartItems.updateQuantity(index, 0);
      return;
    }

    input.value = newVal;
    // Trigger change event so CartItems picks it up
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Make the header cart icon open this drawer
   */
  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  /**
   * Open the cart drawer
   * Uses iOS-safe scroll lock: position fixed + top offset
   */
  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    // iOS-safe body scroll lock
    this.savedScrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.savedScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.classList.add('cart-drawer-open');

    // Trigger animation
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty') || this.querySelector('.cart-drawer__empty')
          : document.getElementById('CartDrawer');
        const focusElement =
          this.querySelector('.cart-drawer__inner') || this.querySelector('[data-cart-close]');
        if (typeof trapFocus === 'function') {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );
  }

  /**
   * Close the cart drawer
   * Restores scroll position after removing scroll lock
   */
  close() {
    this.classList.remove('active', 'animate');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }

    // Restore body scroll
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.classList.remove('cart-drawer-open');
    window.scrollTo(0, this.savedScrollY);
  }

  /**
   * Render fresh cart drawer contents from Section Rendering API response.
   * Called by product-form.js after add-to-cart.
   */
  renderContents(parsedState) {
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    // Toggle empty state
    const isEmpty = parsedState.item_count === 0;
    this.classList.toggle('is-empty', isEmpty);
    const drawerInner = this.querySelector('.cart-drawer__inner');
    if (drawerInner) {
      drawerInner.classList.toggle('is-empty', isEmpty);
    }

    // Bounce the cart badge
    this.triggerBadgeBounce();

    // Announce for screen readers
    this.announceCartChange('added');

    // Auto-open the drawer
    setTimeout(() => {
      this.open();
    });
  }

  /**
   * Handle free sample selection (CART-06).
   * Only one sample allowed per cart — swaps if one already exists.
   * Samples are identified by _sample line item property.
   */
  handleSampleSelect(button) {
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    // Disable all sample buttons during operation
    this.querySelectorAll('[data-sample-add]').forEach((btn) => {
      btn.disabled = true;
    });
    button.textContent = '…';

    // Check if an existing sample needs to be removed first (swap)
    const samplesContainer = this.querySelector('[data-cart-samples]');
    const existingSampleKey = samplesContainer ? samplesContainer.dataset.sampleKey : '';

    const addNewSample = () => {
      return fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: parseInt(variantId, 10), quantity: 1, properties: { _sample: 'true' } }],
        }),
      })
        .then((r) => r.json())
        .then((data) => {
          if (data.status) {
            // Shopify error response
            console.error('Sample add error:', data.description);
            this.resetSampleButtons();
            return;
          }
          this.refreshAndOpen();
        });
    };

    if (existingSampleKey) {
      // Swap: remove old sample, then add new one
      fetch(window.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: existingSampleKey, quantity: 0 }),
      })
        .then((r) => r.json())
        .then(() => addNewSample())
        .catch((e) => {
          console.error('Sample swap failed:', e);
          this.resetSampleButtons();
        });
    } else {
      addNewSample().catch((e) => {
        console.error('Sample add failed:', e);
        this.resetSampleButtons();
      });
    }
  }

  /**
   * Reset sample buttons to their default state after an error.
   */
  resetSampleButtons() {
    this.querySelectorAll('[data-sample-add]').forEach((btn) => {
      btn.disabled = false;
      if (!btn.classList.contains('is-selected')) {
        btn.textContent =
          (window.cartStrings && window.cartStrings.sampleSelect) ||
          'Seç';
      }
    });
  }

  /**
   * Handle upsell quick-add (CART-07).
   * Adds product to cart via AJAX and refreshes drawer.
   */
  handleUpsellAdd(button) {
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    // Loading state
    button.disabled = true;
    const originalText = button.textContent;
    button.textContent = '…';

    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: parseInt(variantId, 10), quantity: 1 }],
      }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.status) {
          // Shopify error response — translate and show
          const msg = window.WRBeautiful.translateCartError(data.description);
          console.error('Upsell add error:', msg);
          button.disabled = false;
          button.textContent = originalText;
          return;
        }
        this.refreshAndOpen();
      })
      .catch((e) => {
        console.error('Upsell add failed:', e);
        button.disabled = false;
        button.textContent = originalText;
      });
  }

  /**
   * Trigger bounce animation on header cart count badge.
   * Uses reflow trick to re-trigger animation on rapid adds.
   * Class name matches header.liquid's @keyframes wr-badge-bounce.
   */
  triggerBadgeBounce() {
    const badge = document.querySelector('.cart-count-bubble');
    if (!badge) return;
    badge.classList.remove('is-bouncing');
    // Force reflow to re-trigger animation
    void badge.offsetWidth;
    badge.classList.add('is-bouncing');
  }

  /**
   * Announce cart change via aria-live region for screen readers.
   * Messages use Turkish data attributes from #cart-live-region in theme.liquid.
   * @param {'added'|'removed'|'updated'} action — The type of cart change
   * @param {string} [productTitle] — Optional product title for announcements
   */
  announceCartChange(action, productTitle) {
    const liveRegion = document.getElementById('cart-live-region');
    if (!liveRegion) return;

    let message = '';
    switch (action) {
      case 'added':
        message = liveRegion.dataset.msgAdded || 'Ürün sepete eklendi';
        if (productTitle) message = productTitle + ' ' + message.toLowerCase();
        break;
      case 'removed':
        message = liveRegion.dataset.msgRemoved || 'Ürün sepetten çıkarıldı';
        if (productTitle) message = productTitle + ' ' + message.toLowerCase();
        break;
      case 'updated':
        message = liveRegion.dataset.msgUpdated || 'Sepet güncellendi';
        break;
      default:
        message = liveRegion.dataset.msgUpdated || 'Sepet güncellendi';
    }

    liveRegion.textContent = message;

    // Clear after 5 seconds
    clearTimeout(this._liveRegionTimeout);
    this._liveRegionTimeout = setTimeout(function () {
      liveRegion.textContent = '';
    }, 5000);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

/**
 * Cart Drawer Items — extends CartItems for drawer-specific section rendering.
 * Overrides getSectionsToRender to update the drawer panel + header cart icon.
 */
class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.cart-drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/**
 * Turkish error message mapping utility.
 * Maps Shopify's English AJAX error descriptions to Turkish as a safety net.
 * Used by CartDrawer to ensure no English text is ever shown to customers.
 */
window.WRBeautiful = window.WRBeautiful || {};
window.WRBeautiful.translateCartError = function (description) {
  if (!description || typeof description !== 'string') {
    return (window.cartStrings && window.cartStrings.error) || 'Bir hata oluştu. Lütfen tekrar deneyin.';
  }

  // Check if already Turkish (contains Turkish-specific characters)
  if (/[ışçğöüİŞÇĞÖÜ]/.test(description)) {
    return description;
  }

  // English → Turkish error mapping (regex patterns for flexible matching)
  const errorMap = [
    { pattern: /you can.?t add more/i, key: 'notEnoughStock' },
    { pattern: /all \d+ .* are in your cart/i, key: 'outOfStock' },
    { pattern: /is not available/i, key: 'outOfStock' },
    { pattern: /sold out/i, key: 'soldOut' },
    { pattern: /cannot find variant/i, key: 'variantNotFound' },
    { pattern: /quantity must be/i, key: 'invalidQuantity' },
    { pattern: /cart error/i, key: 'error' },
    { pattern: /product not found/i, key: 'productNotFound' },
    { pattern: /not enough/i, key: 'notEnoughStock' },
    { pattern: /maximum/i, key: 'aboveMaximum' },
    { pattern: /minimum/i, key: 'belowMinimum' },
    { pattern: /network/i, key: 'networkError' },
    { pattern: /server/i, key: 'serverError' },
  ];

  if (window.cartStrings) {
    for (const { pattern, key } of errorMap) {
      if (pattern.test(description) && window.cartStrings[key]) {
        return window.cartStrings[key];
      }
    }
  }

  return (window.cartStrings && window.cartStrings.error) || 'Bir hata oluştu. Lütfen tekrar deneyin.';
};
